import React from 'react';
import { Box, Typography, Tooltip, IconButton, Button } from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import { Sparkle, ArrowUp } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { getEstimatedCost } from '../../utils/aiTokenConstants';
import useThemeStyles from '../../theme/useThemeStyles';

const buildCoupleMessageLimitTooltip = (creditsInfo) => {
  const base = creditsInfo?.messageLimitBase;
  const addon = creditsInfo?.messageLimitAddon || 0;
  const planKey = creditsInfo?.couplesPlanKey;
  const baseNum = typeof base === 'number' ? base : null;

  if (baseNum != null && addon > 0) {
    if (planKey === 'complete') {
      return `${baseNum.toLocaleString()} Complete messages + ${addon.toLocaleString()} AI message bundle`;
    }
    const planLabel = planKey === 'free' ? 'Free plan' : 'plan';
    return `${baseNum.toLocaleString()} ${planLabel} messages + ${addon.toLocaleString()} AI message bundle`;
  }
  if (baseNum != null) {
    if (planKey === 'free') {
      return `${baseNum.toLocaleString()} messages included with your Free plan each billing period.`;
    }
    if (planKey === 'complete') {
      return `${baseNum.toLocaleString()} messages included with your Complete plan each billing period.`;
    }
    return `${baseNum.toLocaleString()} messages included with your plan each billing period.`;
  }
  const total = creditsInfo?.messageLimit;
  return typeof total === 'number'
    ? `${total.toLocaleString()} messages per billing period (plan allowance).`
    : 'Message allowance for this billing period.';
};

const TokenCounter = ({ creditsInfo, loading, onPurchaseClick, initialCredits, mode }) => {
  const navigate = useNavigate();
  const styles = useThemeStyles();

  const isSupplier = mode === 'supplier';
  const isCouple = mode === 'couple';
  const unlimitedMessages = creditsInfo?.unlimitedMessages === true;
  // Suppliers and couples: use API monthly message pool when the plan enforces a finite cap
  const hasMessageLimit = Number.isFinite(creditsInfo?.messageLimit) && !unlimitedMessages;

  // Fallback when not on a capped message plan (e.g. trial): estimate from credit balance
  const averageCreditsPerMessage = getEstimatedCost('message').credits;

  const calculateMessages = (credits) => {
    if (!credits || credits <= 0) return 0;
    return Math.floor(credits / averageCreditsPerMessage);
  };

  const totalMessages = hasMessageLimit
    ? creditsInfo.messageLimit
    : unlimitedMessages
      ? Infinity
      : calculateMessages(initialCredits);
  const remainingMessages = hasMessageLimit
    ? creditsInfo.messagesRemaining
    : unlimitedMessages
      ? Infinity
      : creditsInfo
        ? calculateMessages(creditsInfo.remainingCredits)
        : 0;
  const messagesUsed = hasMessageLimit ? creditsInfo.messagesUsed : null;

  if (loading || !creditsInfo) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
          {unlimitedMessages ? (
            'Unlimited messages'
          ) : hasMessageLimit ? (
            `${remainingMessages}/${totalMessages} messages remaining`
          ) : (
            `${remainingMessages}/${totalMessages} messages remaining`
          )}
        </Typography>
        <Tooltip
          title={
            hasMessageLimit && isSupplier
              ? `Message limit: ${totalMessages.toLocaleString()} per business/month (shared across all team members). Used: ${messagesUsed?.toLocaleString() || 0}.`
              : hasMessageLimit && isCouple
                ? buildCoupleMessageLimitTooltip(creditsInfo)
                : `Messages are estimated from your credit balance (~${averageCreditsPerMessage} credits per message on average). Actual usage may vary. Purchase more credits when you run low.`
          }
          arrow
        >
          <IconButton size="small" sx={{ p: 0.25 }}>
            <InfoIcon fontSize="small" sx={{ fontSize: '0.875rem' }} />
          </IconButton>
        </Tooltip>
      </Box>
      {hasMessageLimit && remainingMessages === 0 && (
        <Button
          variant="contained"
          size="small"
          onClick={() => navigate(isSupplier ? '/supplier/billing' : '/billing')}
          startIcon={<ArrowUp size={14} />}
          sx={{ 
            ml: 'auto',
            fontSize: '0.7rem',
            py: 0.25,
            px: 1,
            minWidth: 'auto',
            ...(styles?.button?.primary || {})
          }}
        >
          {isSupplier ? 'Upgrade Plan' : 'Manage plan'}
        </Button>
      )}
      {!hasMessageLimit && creditsInfo.remainingCredits < 50 && (
        <Button
          variant="outlined"
          size="small"
          onClick={onPurchaseClick}
          startIcon={<Sparkle weight="fill" size={14} />}
          sx={{ 
            ml: 'auto',
            fontSize: '0.7rem',
            py: 0.25,
            px: 1,
            minWidth: 'auto'
          }}
        >
          Buy Credits
        </Button>
      )}
    </Box>
  );
};

export default TokenCounter;
